import { Box, CircularProgress, TextField, Typography } from '@mui/material';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';
import { ErrorSnackbar } from '../components/ErrorSnackbar';
import { FormHeaderImage, FormHeaderWrapper, FormLoadingOverlay, FormPaper } from '../components/FormStyledComponents';
import { PrivacyConsentSection } from '../components/PrivacyConsentSection';
import { useHeaderImage } from '../hooks/useHeaderImage';
import { LanscoButton } from '../lansco-button';
import { FORM_IDS } from '../utils/formAssets';
import { submitFormToApi } from '../utils/submitForm';

// Form validation and types
interface FormValues {
  name: string;
  email: string;
  phone: string;
  company: string;
  consent: boolean;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Name ist erforderlich'),
  email: Yup.string()
    .email('Ungültige E-Mail-Adresse')
    .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, 'Ungültiges E-Mail-Format')
    .required('E-Mail ist erforderlich'),
  phone: Yup.string(),
  company: Yup.string(),
  consent: Yup.boolean()
    .required('Bitte stimmen Sie den Datenschutzbestimmungen zu')
    .oneOf([true], 'Du musst den Datenschutzbestimmungen zustimmen, um deinen Zugang zu erhalten.'),
});

const initialValues: FormValues = {
  name: '',
  email: '',
  phone: '',
  company: '',
  consent: false,
};

// API endpoint
const API_ENDPOINT = 'https://3579fdf80154ea27b4d1f7c7d4a13a.57.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/d0d24b11fcfc4cc0892e1938218cb4e4/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=fYnDpFZ3uPsMOWh3s2weWLp0hD51seYncWJx7Nbkx34';

export const IKP3M = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const headerImage = useHeaderImage(FORM_IDS.IKP3M);

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    setIsLoading(true);
    setErrorMessage('');
    setShowErrorSnackbar(false);

    try {
      const result = await submitFormToApi({
        apiEndpoint: API_ENDPOINT,
        payload: {
          name: values.name,
          mail: values.email,
          phone: values.phone,
          company: values.company,
          consent: values.consent,
        },
        debugFieldValue: values.name,
      });

      if (result.success) {
        setIsSubmitted(true);
      } else {
        setErrorMessage(result.error!);
        setShowErrorSnackbar(true);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setErrorMessage('Beim Absenden des Formulars ist ein Fehler aufgetreten. Bitte versuche es erneut.');
      setShowErrorSnackbar(true);
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <>
      <FormHeaderWrapper>
        <FormHeaderImage src={headerImage} alt="Lansco Logo" />
      </FormHeaderWrapper>
      <FormPaper elevation={3}>
        {!isSubmitted ? (
          <>
            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 300, mb: 3, color: 'primary.main' }}>
              Dein Testzugang für 3 Monate
            </Typography>
            <Typography variant="body1" component="div" sx={{ mb: 2, lineHeight: 1.6 }}>
              Im Microsoft-Kosmos kommt es monatlich zu einigen Änderungen und Neuerungen. Den Überblick zu behalten fällt häufig schwer und ist mit erheblichem Aufwand verbunden. Hier schaffen wir Abhilfe. Die Innovative Kaffeepause fasst alle monatlichen Änderungen kompakt als Video on Demand zusammen.
            </Typography>
            <Typography variant="h6" component="h2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
              Was ist drin?
            </Typography>
            <Typography variant="body2" component="ul" sx={{ mb: 2, pl: 2, '& li': { mb: 1 } }}>
              <li>20-30 Minuten Video zu allen Neuerungen, Änderungen und Ankündigungen</li>
              <li>Die Möglichkeit, Neuerungen und Änderungen strategisch mit den Beratern der LANSCO zu besprechen.</li>
              <li>Für alle Neukunden der Innovativen Kaffeepause!</li>
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
              Hol Dir jetzt deine 3 kostenlosen Monate der Innovativen Kaffeepause und bleibe immer auf dem aktuellen Stand!
            </Typography>
            {isLoading && (
              <FormLoadingOverlay>
                <CircularProgress size={60} sx={{ color: 'primary.main' }} />
              </FormLoadingOverlay>
            )}
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              validateOnMount={true}
              initialTouched={{
                consent: true
              }}
            >
              {({ errors, touched, isValid, dirty }) => (
                <Form>
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="name"
                    label="Name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="email"
                    label="E-Mail"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="phone"
                    label="Telefonnummer"
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="company"
                    label="Unternehmen"
                    error={touched.company && Boolean(errors.company)}
                    helperText={touched.company && errors.company}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <PrivacyConsentSection
                    label="Ja, ich stimme zu, weil nur dann kann ich einen kostenlosen Zugang für 3 Monate erhalten"
                    isLoading={isLoading}
                  />
                  {errorMessage && (
                    <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                      {errorMessage}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <LanscoButton
                      type="submit"
                      disabled={!(isValid && dirty) || isLoading}
                      sx={{ minWidth: 180 }}
                    >
                      {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Anfrage senden'}
                    </LanscoButton>
                  </Box>
                </Form>
              )}
            </Formik>
          </>
        ) : (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 300 }}>
              Vielen Dank!
            </Typography>
            <Typography variant="body1" sx={{ mb: 4 }}>
              Deine Anfrage wurde erfolgreich empfangen. In den nächsten 24 Std. erhältst Du deine Zugangsdaten zur LANSCO academy per Mail.
            </Typography>
            <LanscoButton
              variant='white'
              onClick={() => window.open('https://www.lansco.de/workshops/lansco-academy.html', '_blank', 'noopener,noreferrer')}
              sx={{ minWidth: 220 }}
            >
              Mehr Infos zur LANSCO academy
            </LanscoButton>
          </Box>
        )}
      </FormPaper>
      <ErrorSnackbar
        open={showErrorSnackbar}
        message={errorMessage}
        onClose={() => setShowErrorSnackbar(false)}
      />
    </>
  );
};
